/**
 * main.tsx — Nexus Client Portal entry point.
 *
 * CP-047 Phase 3: Provider selection via VITE_AUTH_PROVIDER build-time env var.
 *
 * VITE_AUTH_PROVIDER=msal (default — current live deployment):
 *   - Renders App + MsalAuthProvider (unchanged from CP-039/CP-045).
 *   - @azure/msal-browser and @azure/msal-react are included in the bundle.
 *   - This is the live production path. DO NOT change this path until cutover.
 *
 * VITE_AUTH_PROVIDER=supertokens (new — CP-047 Phase 3):
 *   - Renders AppSuperTokens + SuperTokensAuthProvider.
 *   - @azure/msal-browser and @azure/msal-react are NOT imported → excluded
 *     from the bundle by Vite's dead-code elimination.
 *   - MSAL files remain in the repo for reference and rollback; they are just
 *     not imported in this build path.
 *
 * CUTOVER INSTRUCTION (Phase 4):
 *   Set VITE_AUTH_PROVIDER=supertokens in the GitHub Environment secret for
 *   the production client-portal deployment, then trigger the deploy workflow.
 *   The MSAL build path remains available for rollback: revert the env var.
 *   Do NOT decommission External ID app regs until the cutover is verified.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
// CP-053 (Nexus OS design refresh, DEC-PORTAL-056): Inter is now the UI font, self-hosted via
// @fontsource — same self-hosted-npm-package mechanism as the IBM Plex fonts it replaces (never
// a Google Fonts <link> — see below for why the previous CDN-link pattern was a silent no-op
// under this app's CSP). Weights match docs/DESIGN.md's type scale (400 body, 500
// subheadings/labels, 600 headings, 700 available — no bold used in the UI today).
//
// Both @fontsource/ibm-plex-sans and @fontsource/ibm-plex-mono are fully removed from this app
// (unlike apps/portal, which keeps ibm-plex-sans — see DEC-PORTAL-038 in apps/portal/DECISIONS.md):
// a repo-wide grep found zero remaining hard-coded "IBM Plex Sans" references anywhere in
// apps/client-portal/src/**, and only two hard-coded "IBM Plex Mono" references
// (apps/client-portal/src/features/client-portal/screens/{Raid,Deliverables}.tsx — out of
// portal-builder's write scope; CR filed to root .changerequest/in/), an accepted cosmetic-only
// mono-fallback-degradation risk per technical-design.md §2.9 (mono removal is non-negotiable
// regardless — the new system's mono token is a system font stack, no webfont needed).
//
// CP-051 regression this must not reintroduce: apps/client-portal/index.html documents that a
// prior Google Fonts <link> silently failed to load under this app's CSP (font-src 'self' only)
// — self-hosting via @fontsource is the proven-working mechanism.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@nexus/client-portal-ui/portal-styles.css';
import './core/index.css';

// Build-time constant — Vite replaces this at bundle time.
// The unused branch is eliminated by Vite's tree-shaker.
const AUTH_PROVIDER = (import.meta.env.VITE_AUTH_PROVIDER as string | undefined) ?? 'msal';

const root = document.getElementById('root');
if (!root) {
  throw new Error('Root element #root not found');
}

if (AUTH_PROVIDER === 'supertokens') {
  // SuperTokens path: no MSAL imports. SessionAuth + custom onboarding UI.
  // Import is dynamic so Vite statically eliminates it from the MSAL bundle.
  void (async () => {
    const { default: AppSuperTokens } = await import('./core/AppSuperTokens');
    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <AppSuperTokens />
      </React.StrictMode>,
    );
  })();
} else {
  // MSAL path (default / current live): MsalAuthProvider + App unchanged.
  void (async () => {
    const { default: App } = await import('./core/App');
    const { MsalAuthProvider } = await import('./core/auth/MsalAuthProvider');
    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <MsalAuthProvider>
          <App />
        </MsalAuthProvider>
      </React.StrictMode>,
    );
  })();
}
